<div
  #tableContainer
  class="mat-table-container"
  [class.height-mode-fill-parent]="
    (appearance().heightMode ?? 'auto') === 'fill-parent'
  "
  [class.height-mode-auto]="(appearance().heightMode ?? 'auto') === 'auto'"
  [class.columns-dragging]="columnDragActive()"
>
  @if (columnDragGhost(); as ghost) {
    <div
      class="column-drag-ghost"
      [style.left.px]="ghost.x"
      [style.top.px]="ghost.y"
    >
      <mat-icon class="column-drag-ghost-icon">drag_indicator</mat-icon>
      <span>{{ ghost.label }}</span>
    </div>
  }
  <!-- Loading spinner overlay -->
  @if (isLoading()) {
    <div class="loading-overlay">
      <mat-spinner diameter="40"></mat-spinner>
    </div>
  }

  <!-- Table wrapper for scrolling -->
  <div
    #tableWrapper
    class="table-wrapper"
    cdkScrollable
    [class.no-paginator]="!showPagination()"
  >
    <!-- Floating row-detail overlays. `.row-detail-floats` is `position: sticky;
         left: 0; height: 0` so it stays anchored at the visible left edge of
         `.table-wrapper` during horizontal scroll while still flowing
         vertically with the rows. It is placed BEFORE the `<table>` so its
         natural y=0 in the scroll content aligns with the table's top - the
         `[style.top.px]` value computed in TS is relative to `.table-wrapper`
         and therefore matches exactly. Each `.row-detail-float` is positioned
         absolutely over its row's spacer cell. -->
    @if (rowExpansion().enabled) {
      <div class="row-detail-floats">
        @for (row of detailFloatRows(); track getRowId(row)) {
          <div
            #rowDetailFloat
            class="row-detail-float"
            [class.row-detail-float-offscreen]="!isRenderedRow(row)"
            [attr.data-float-row-id]="getRowId(row)"
            [style.top.px]="getDetailFloatTop(row)"
          >
            <div
              class="row-detail-wrapper"
              [class.row-detail-wrapper-expanded]="isRowDetailVisible(row)"
              [class.row-detail-wrapper-closing]="isRowClosing(row)"
              [attr.data-row-id]="getRowId(row)"
              [style.max-height.px]="getRowDetailMaxHeightPx(row)"
            >
              <div
                class="row-detail-container"
                [style.height]="rowExpansion().detailHeight"
              >
                @if (table().printSubject(); as subject) {
                  <app-row-print-button
                    class="row-detail-print"
                    [subject]="subject"
                    [recordId]="$any(row).id"
                  />
                }
                <ng-container #rowDetailContainer></ng-container>
              </div>
            </div>
          </div>
        }
      </div>
    }

    @if (virtualTopPad() > 0) {
      <div
        class="virtual-scroll-pad"
        [style.height.px]="virtualTopPad()"
        aria-hidden="true"
      ></div>
    }

    @if (dragDrop().enabled) {
      <div
        cdkDropList
        [cdkDropListData]="data()"
        (cdkDropListDropped)="handleDragDrop($event)"
        class="cdk-drop-list-container"
      >
        <table
          mat-table
          [dataSource]="dataSource"
          multiTemplateDataRows
          matSort
          [matSortActive]="matSortActive()"
          [matSortDirection]="matSortDirection()"
          (matSortChange)="onSortChange($event)"
          [trackBy]="trackByIndex"
          class="mat-elevation-z0 table-container"
          [class.striped]="appearance().striped"
          [class.bordered]="appearance().bordered"
          [class.dense]="appearance().dense"
          [class.hoverable]="appearance().showRowHover"
          [class.columns-dragging]="columnDragActive()"
          [class.column-reorder-animating]="columnReorderAnimating()"
        >
          <!-- Selection Column -->
          @if (table().selection().enabled) {
            <ng-container matColumnDef="select" sticky>
              <th mat-header-cell *matHeaderCellDef class="select-column">
                @if (table().selection().mode === 'multiple') {
                  <mat-checkbox
                    [checked]="isAllSelected()"
                    [indeterminate]="isSomeSelected()"
                    (change)="onSelectAllChange()"
                    (click)="$event.stopPropagation()"
                    [attr.aria-label]="
                      'components.mat-table.selectAll' | transloco
                    "
                  ></mat-checkbox>
                }
              </th>
              <td mat-cell *matCellDef="let row" class="select-column">
                @if (table().selection().mode === 'multiple') {
                  <mat-checkbox
                    [checked]="table().isRowSelected(row)"
                    (change)="onSelectionChange(row)"
                    (click)="$event.stopPropagation()"
                    [attr.aria-label]="
                      'components.mat-table.selectRow' | transloco
                    "
                  ></mat-checkbox>
                } @else {
                  <mat-radio-button
                    [checked]="table().isRowSelected(row)"
                    (change)="onSelectionChange(row)"
                    (click)="$event.stopPropagation()"
                    [value]="getRowId(row)"
                    name="table-row-selection"
                    [attr.aria-label]="
                      'components.mat-table.selectRow' | transloco
                    "
                  ></mat-radio-button>
                }
              </td>
            </ng-container>
          }

          <!-- Row Number Column -->
          @if (appearance().showRowNumbers) {
            <ng-container matColumnDef="rowNumber" sticky>
              <th mat-header-cell *matHeaderCellDef class="row-number-column">
                @if (appearance().rowNumberHeaderTemplate) {
                  <ng-container
                    *ngTemplateOutlet="appearance().rowNumberHeaderTemplate!"
                  ></ng-container>
                } @else {
                  {{ 'components.mat-table.rowNumber' | transloco }}
                }
              </th>
              <td mat-cell *matCellDef="let row" class="row-number-column">
                {{ getRowNumber(row) }}
              </td>
            </ng-container>
          }

          <!-- Expand Column -->
          @if (rowExpansion().enabled && !rowExpansion().hideExpandColumn) {
            <ng-container matColumnDef="expand" sticky>
              <th mat-header-cell *matHeaderCellDef class="expand-column">
                @if (rowExpansion().expansionHeaderTemplate) {
                  <ng-container
                    *ngTemplateOutlet="rowExpansion().expansionHeaderTemplate!"
                  ></ng-container>
                }
              </th>
              <td mat-cell *matCellDef="let row" class="expand-column">
                @if (isExpandableRow(row)) {
                  <mat-icon
                    (click)="toggleRow(row); $event.stopPropagation()"
                    [attr.aria-label]="
                      'components.mat-table.expandRow' | transloco
                    "
                    class="expand-icon"
                  >
                    {{ isRowExpanded(row) ? 'expand_less' : 'expand_more' }}
                  </mat-icon>
                }
              </td>
            </ng-container>
          }

          <!-- Child Toggle Column -->
          @if (
            childRows().enabled &&
            !childRows().embedToggleInFirstColumn &&
            !childRows().hideBuiltInChildToggle
          ) {
            <ng-container matColumnDef="childToggle" sticky>
              <th
                mat-header-cell
                *matHeaderCellDef
                class="child-toggle-column"
              ></th>
              <td
                mat-cell
                *matCellDef="let row"
                class="child-toggle-column"
                [ngStyle]="getChildToggleCellStyle(row)"
              >
                @if (showChildTreeToggleIcon(row)) {
                  <mat-icon
                    (click)="
                      onEmbeddedTreeToggleClick(row); $event.stopPropagation()
                    "
                    [attr.aria-label]="
                      isParentRow(row)
                        ? ('components.mat-table.toggleChildren' | transloco)
                        : ('components.mat-table.expandRow' | transloco)
                    "
                    class="child-toggle-icon"
                  >
                    @if (isParentRow(row)) {
                      {{
                        areChildRowsVisible(row)
                          ? 'keyboard_arrow_down'
                          : 'keyboard_arrow_right'
                      }}
                    } @else {
                      {{
                        isRowExpanded(row)
                          ? 'keyboard_arrow_down'
                          : 'keyboard_arrow_right'
                      }}
                    }
                  </mat-icon>
                }
              </td>
            </ng-container>
          }

          <!-- Data Columns -->
          @for (
            column of orderedColumns();
            track columnLayoutTrack($index, column);
            let dataColIdx = $index
          ) {
            <ng-container
              [matColumnDef]="column.id"
              [stickyEnd]="column.stickyEnd"
            >
              <!-- Header -->
              <th
                mat-header-cell
                *matHeaderCellDef
                appResizableColumn
                [class.column-drop-highlight]="isColumnDropHighlighted(column)"
                [class.column-dragging-source]="isColumnDragSource(column)"
                [class.column-drag-over]="isColumnDragOver(column)"
                [mat-sort-header]="column.id"
                [disabled]="!isColumnSortable(column) || columnDragActive()"
                [style.width]="getColumnWidth(column)"
                [style.min-width]="column.minWidth"
                [style.max-width]="column.maxWidth"
                [attr.data-column-id]="column.id"
                [resizable]="isColumnResizable(column)"
                [minWidth]="getMinWidth(column)"
                [maxWidth]="getMaxWidth(column)"
                (resizeStart)="onColumnResizeStart(column.id)"
                (resizing)="onColumnResizing(column.id, $event)"
                (resizeEnd)="onColumnResizeEnd(column.id, $event)"
                [class.header-align-left]="!column.headerAlign"
                [class.header-align-center]="column.headerAlign === 'center'"
                [class.header-align-right]="column.headerAlign === 'right'"
                [class.header-align-left]="column.headerAlign === 'left'"
                [ngClass]="column.headerClass"
                [matTooltip]="translateHeader(column.label)"
                [matTooltipDisabled]="!column.label"
                matTooltipPosition="above"
                [matTooltipShowDelay]="0"
              >
                <div
                  class="header-content"
                  [class.column-draggable]="isColumnDraggable(column)"
                >
                  @if (isColumnDraggable(column)) {
                    <mat-icon
                      class="column-drag-handle"
                      aria-hidden="true"
                      (pointerdown)="
                        onColumnDragHandlePointerDown(
                          $event,
                          column,
                          dataColIdx
                        )
                      "
                    >
                      drag_indicator
                    </mat-icon>
                  }
                  <!-- Custom header template or default label -->
                  @if (column.headerTemplate) {
                    <ng-container
                      *ngTemplateOutlet="column.headerTemplate"
                    ></ng-container>
                  } @else {
                    <span class="header-label" [ngClass]="column.headerClass">
                      {{ translateHeader(column.label) }}
                    </span>
                  }

                  <!-- Filter button -->
                  @if (column.filterable) {
                    <button
                      mat-icon-button
                      [matMenuTriggerFor]="filterMenu"
                      #menuTrigger="matMenuTrigger"
                      (click)="$event.stopPropagation()"
                      [class.filter-active]="
                        getColumnFilter(column.id) !== null
                      "
                    >
                      <mat-icon
                        [class.active]="getColumnFilter(column.id) !== null"
                      >
                        filter_list
                      </mat-icon>
                    </button>

                    <!-- Filter menu with column filter component -->
                    <mat-menu #filterMenu="matMenu" class="filter-menu">
                      <ng-template matMenuContent>
                        <div (click)="$event.stopPropagation()">
                          <app-column-filter
                            [columnId]="column.id"
                            [columnLabel]="translateHeader(column.label)"
                            [filterType]="
                              $any(column).filterType ||
                              ($any(column).filterAdvancedSearchConfig
                                ? 'advancedSearch'
                                : 'text')
                            "
                            [currentFilter]="getColumnFilter(column.id)"
                            [filterOptions]="column.filterOptions || []"
                            [advancedSearchConfig]="
                              $any(column).filterAdvancedSearchConfig ?? null
                            "
                            [advancedSearchCanEdit]="
                              $any(column).filterAdvancedSearchCanEdit === true
                            "
                            [filterDynamicList]="
                              $any(column).filterDynamicList ?? null
                            "
                            [allowedOperators]="
                              $any(column).allowedOperators ?? null
                            "
                            [menuTrigger]="menuTrigger"
                            (filterApplied)="onFilterApplied(column.id, $event)"
                            (filterCleared)="onFilterCleared(column.id)"
                          ></app-column-filter>
                        </div>
                      </ng-template>
                    </mat-menu>
                  }
                </div>
              </th>

              <!-- Cell -->
              <td
                mat-cell
                *matCellDef="let row; let i = index"
                [attr.data-column-id]="column.id"
                [class]="getCellClass(row, column)"
                [class.column-drop-highlight]="isColumnDropHighlighted(column)"
                [class.column-drag-over]="isColumnDragOver(column)"
                [class.tree-embed-first-col]="
                  shouldEmbedChildToggleInCell(dataColIdx)
                "
                [style.width]="getColumnWidth(column)"
                [style.min-width]="column.minWidth || '50px'"
                [style.text-align]="column.cellsAlign || 'left'"
                [attr.data-column-id]="column.id"
                [ngStyle]="
                  getChildRowCellStyle(
                    row,
                    getDataColumnGlobalIndex(dataColIdx)
                  )
                "
              >
                @if (shouldEmbedChildToggleInCell(dataColIdx)) {
                  <div
                    class="tree-first-col-inner"
                    [ngStyle]="getEmbeddedTreeIndentStyle(row)"
                  >
                    @if (
                      !childRows().hideBuiltInChildToggle &&
                      showChildTreeToggleIcon(row)
                    ) {
                      <mat-icon
                        (click)="
                          onEmbeddedTreeToggleClick(row);
                          $event.stopPropagation()
                        "
                        [attr.aria-label]="
                          isParentRow(row)
                            ? ('components.mat-table.toggleChildren'
                              | transloco)
                            : ('components.mat-table.expandRow' | transloco)
                        "
                        class="child-toggle-icon"
                      >
                        @if (isParentRow(row)) {
                          {{
                            areChildRowsVisible(row)
                              ? 'keyboard_arrow_down'
                              : 'keyboard_arrow_right'
                          }}
                        } @else {
                          {{
                            isRowExpanded(row)
                              ? 'keyboard_arrow_down'
                              : 'keyboard_arrow_right'
                          }}
                        }
                      </mat-icon>
                    } @else if (!childRows().hideBuiltInChildToggle) {
                      <span
                        class="tree-toggle-spacer"
                        aria-hidden="true"
                      ></span>
                    }
                    <span class="tree-first-col-value">
                      @if (column.cellTemplate) {
                        <ng-container
                          *ngTemplateOutlet="
                            column.cellTemplate;
                            context: {
                              $implicit: row,
                              row: row,
                              column: column,
                              value: getCellValue(row, column),
                              index: i,
                            }
                          "
                        ></ng-container>
                      } @else {
                        <span
                          [matTooltip]="getCellTooltip(row, column)"
                          [matTooltipDisabled]="!getCellTooltip(row, column)"
                          matTooltipPosition="above"
                          [matTooltipShowDelay]="0"
                        >
                          {{ getCellValue(row, column) }}
                        </span>
                      }
                    </span>
                  </div>
                } @else {
                  @if (column.cellTemplate) {
                    <ng-container
                      *ngTemplateOutlet="
                        column.cellTemplate;
                        context: {
                          $implicit: row,
                          row: row,
                          column: column,
                          value: getCellValue(row, column),
                          index: i,
                        }
                      "
                    ></ng-container>
                  } @else {
                    <span
                      [matTooltip]="getCellTooltip(row, column)"
                      [matTooltipDisabled]="!getCellTooltip(row, column)"
                      matTooltipPosition="above"
                      [matTooltipShowDelay]="0"
                    >
                      {{ getCellValue(row, column) }}
                    </span>
                  }
                }
              </td>
            </ng-container>
          }

          <!-- Actions Column - only show if enabled and has visible actions -->
          @if (table().hasVisibleActions()) {
            <ng-container matColumnDef="actions" stickyEnd>
              <th
                mat-header-cell
                *matHeaderCellDef
                class="actions-column"
                [style.--actions-column-width]="getActionsColumnWidth()"
                [hidden]="!showActionsHeader()"
              >
                @if (actions().headerTemplate) {
                  <ng-container
                    *ngTemplateOutlet="actions().headerTemplate!"
                  ></ng-container>
                }
              </th>
              <td
                mat-cell
                *matCellDef="let row"
                class="actions-column"
                [style.--actions-column-width]="getActionsColumnWidth()"
                [hidden]="!showActions()"
              >
                @if (actions().template) {
                  <ng-container
                    *ngTemplateOutlet="
                      actions().template!;
                      context: { row: row }
                    "
                  ></ng-container>
                } @else if (actions().actions) {
                  @for (action of actions().actions; track $index) {
                    @if (!action.visible || action.visible(row)) {
                      @if (action.icon) {
                        <app-button
                          variant="icon"
                          [type]="action.type ?? 'secondary'"
                          [tooltip]="action.tooltip || action.label || ''"
                          [disabledExternal]="
                            action.disabled ? action.disabled(row) : false
                          "
                          (clicked)="
                            action.handler(row); $event.stopPropagation()
                          "
                        >
                          @if (action.pending?.(row)) {
                            <mat-spinner
                              prefix
                              class="mat-table-action-pending-spinner"
                              diameter="20"
                            />
                          } @else if (action.iconFont) {
                            <mat-icon prefix>{{ action.icon }}</mat-icon>
                          } @else {
                            <mat-icon prefix [svgIcon]="action.icon"></mat-icon>
                          }
                        </app-button>
                      } @else if (action.label) {
                        <app-button
                          variant="text"
                          [label]="action.label"
                          [tooltip]="action.tooltip || ''"
                          [disabledExternal]="
                            action.disabled ? action.disabled(row) : false
                          "
                          (clicked)="
                            action.handler(row); $event.stopPropagation()
                          "
                        />
                      }
                    }
                  }
                }
              </td>
            </ng-container>
          }

          <!-- Expanded Row Detail spacer (height-only; content lives in .row-detail-float) -->
          @if (rowExpansion().enabled) {
            <ng-container matColumnDef="expandedDetail">
              <td
                mat-cell
                *matCellDef="let row"
                [attr.colspan]="layoutDisplayedColumns().length"
                class="expanded-detail-cell"
                [attr.data-spacer-row-id]="getRowId(row)"
                [style.height.px]="getDetailSpacerHeight(row)"
              ></td>
            </ng-container>
          }

          <!-- Header Row -->
          <tr
            mat-header-row
            *matHeaderRowDef="
              dragPreviewDisplayedColumns();
              sticky: !columnReorder().enabled
            "
          ></tr>

          <!-- Data Rows -->
          @if (dragDrop().enabled) {
            <tr
              mat-row
              *matRowDef="let row; columns: dragPreviewDisplayedColumns()"
              cdkDrag
              [cdkDragData]="row"
              [class.clickable]="
                (rowExpansion().enabled &&
                  rowExpansion().expandOnRowClick &&
                  isExpandableRow(row)) ||
                (childRows().enabled &&
                  childRows().toggleOnRowClick &&
                  isParentRow(row))
              "
              [class.expanded]="isRowExpanded(row)"
              [class.striped-even-row]="
                appearance().striped && isStripedEvenRow(row)
              "
              [ngClass]="getRowClass(row)"
              [hidden]="!table().isRowVisible(row)"
              (click)="onRowClick(row, $event)"
            ></tr>
          } @else {
            <tr
              mat-row
              *matRowDef="let row; columns: layoutDisplayedColumns()"
              [class.clickable]="
                (rowExpansion().enabled &&
                  rowExpansion().expandOnRowClick &&
                  isExpandableRow(row)) ||
                (childRows().enabled &&
                  childRows().toggleOnRowClick &&
                  isParentRow(row))
              "
              [class.expanded]="isRowExpanded(row)"
              [class.striped-even-row]="
                appearance().striped && isStripedEvenRow(row)
              "
              [ngClass]="getRowClass(row)"
              [hidden]="!table().isRowVisible(row)"
              (click)="onRowClick(row, $event)"
            ></tr>
          }

          <!-- Expanded Detail Row (only for open/closing rows — avoid 2× DOM at 500/1000) -->
          @if (rowExpansion().enabled) {
            <tr
              mat-row
              *matRowDef="
                let row;
                columns: ['expandedDetail'];
                when: isExpansionDetailRow
              "
              class="detail-row"
            ></tr>
          }
        </table>
        @if (data().length === 0) {
          <div class="no-data-row">
            <div>{{ 'core.noData' | transloco }}</div>
          </div>
        }
      </div>
    } @else {
      <table
        mat-table
        [dataSource]="dataSource"
        multiTemplateDataRows
        matSort
        [matSortActive]="matSortActive()"
        [matSortDirection]="matSortDirection()"
        (matSortChange)="onSortChange($event)"
        [trackBy]="trackByIndex"
        class="mat-elevation-z0 table-container"
        [class.striped]="appearance().striped"
        [class.bordered]="appearance().bordered"
        [class.dense]="appearance().dense"
        [class.hoverable]="appearance().showRowHover"
        [class.columns-dragging]="columnDragActive()"
        [class.column-reorder-animating]="columnReorderAnimating()"
      >
        <!-- Selection Column -->
        @if (table().selection().enabled) {
          <ng-container matColumnDef="select" sticky>
            <th mat-header-cell *matHeaderCellDef class="select-column">
              @if (table().selection().mode === 'multiple') {
                <mat-checkbox
                  [checked]="isAllSelected()"
                  [indeterminate]="isSomeSelected()"
                  (change)="onSelectAllChange()"
                  (click)="$event.stopPropagation()"
                  [attr.aria-label]="
                    'components.mat-table.selectAll' | transloco
                  "
                ></mat-checkbox>
              }
            </th>
            <td mat-cell *matCellDef="let row" class="select-column">
              @if (table().selection().mode === 'multiple') {
                <mat-checkbox
                  [checked]="table().isRowSelected(row)"
                  (change)="onSelectionChange(row)"
                  (click)="$event.stopPropagation()"
                  [attr.aria-label]="
                    'components.mat-table.selectRow' | transloco
                  "
                ></mat-checkbox>
              } @else {
                <mat-radio-button
                  [checked]="table().isRowSelected(row)"
                  (change)="onSelectionChange(row)"
                  (click)="$event.stopPropagation()"
                  [value]="getRowId(row)"
                  name="table-row-selection"
                  [attr.aria-label]="
                    'components.mat-table.selectRow' | transloco
                  "
                ></mat-radio-button>
              }
            </td>
          </ng-container>
        }

        <!-- Row Number Column -->
        @if (appearance().showRowNumbers) {
          <ng-container matColumnDef="rowNumber" sticky>
            <th mat-header-cell *matHeaderCellDef class="row-number-column">
              @if (appearance().rowNumberHeaderTemplate) {
                <ng-container
                  *ngTemplateOutlet="appearance().rowNumberHeaderTemplate!"
                ></ng-container>
              } @else {
                {{ 'components.mat-table.rowNumber' | transloco }}
              }
            </th>
            <td mat-cell *matCellDef="let row" class="row-number-column">
              {{ getRowNumber(row) }}
            </td>
          </ng-container>
        }

        <!-- Expand Column -->
        @if (rowExpansion().enabled && !rowExpansion().hideExpandColumn) {
          <ng-container matColumnDef="expand" sticky>
            <th mat-header-cell *matHeaderCellDef class="expand-column">
              @if (rowExpansion().expansionHeaderTemplate) {
                <ng-container
                  *ngTemplateOutlet="rowExpansion().expansionHeaderTemplate!"
                ></ng-container>
              }
            </th>
            <td mat-cell *matCellDef="let row" class="expand-column">
              @if (isExpandableRow(row)) {
                <mat-icon
                  (click)="toggleRow(row); $event.stopPropagation()"
                  [attr.aria-label]="
                    'components.mat-table.expandRow' | transloco
                  "
                  class="expand-icon"
                >
                  {{ isRowExpanded(row) ? 'expand_less' : 'expand_more' }}
                </mat-icon>
              }
            </td>
          </ng-container>
        }

        <!-- Child Toggle Column -->
        @if (
          childRows().enabled &&
          !childRows().embedToggleInFirstColumn &&
          !childRows().hideBuiltInChildToggle
        ) {
          <ng-container matColumnDef="childToggle" sticky>
            <th
              mat-header-cell
              *matHeaderCellDef
              class="child-toggle-column"
            ></th>
            <td
              mat-cell
              *matCellDef="let row"
              class="child-toggle-column"
              [ngStyle]="getChildToggleCellStyle(row)"
            >
              @if (showChildTreeToggleIcon(row)) {
                <mat-icon
                  (click)="
                    onEmbeddedTreeToggleClick(row); $event.stopPropagation()
                  "
                  [attr.aria-label]="
                    isParentRow(row)
                      ? ('components.mat-table.toggleChildren' | transloco)
                      : ('components.mat-table.expandRow' | transloco)
                  "
                  class="child-toggle-icon"
                >
                  @if (isParentRow(row)) {
                    {{
                      areChildRowsVisible(row)
                        ? 'keyboard_arrow_down'
                        : 'keyboard_arrow_right'
                    }}
                  } @else {
                    {{
                      isRowExpanded(row)
                        ? 'keyboard_arrow_down'
                        : 'keyboard_arrow_right'
                    }}
                  }
                </mat-icon>
              }
            </td>
          </ng-container>
        }

        <!-- Data Columns -->
        @for (
          column of orderedColumns();
          track columnLayoutTrack($index, column);
          let dataColIdx = $index
        ) {
          <ng-container
            [matColumnDef]="column.id"
            [stickyEnd]="column.stickyEnd"
          >
            <!-- Header -->
            <th
              mat-header-cell
              *matHeaderCellDef
              appResizableColumn
              [class.column-drop-highlight]="isColumnDropHighlighted(column)"
              [class.column-dragging-source]="isColumnDragSource(column)"
              [class.column-drag-over]="isColumnDragOver(column)"
              [mat-sort-header]="column.id"
              [disabled]="!isColumnSortable(column) || columnDragActive()"
              [style.width]="getColumnWidth(column)"
              [style.min-width]="column.minWidth"
              [style.max-width]="column.maxWidth"
              [attr.data-column-id]="column.id"
              [resizable]="isColumnResizable(column)"
              [minWidth]="getMinWidth(column)"
              [maxWidth]="getMaxWidth(column)"
              (resizeStart)="onColumnResizeStart(column.id)"
              (resizing)="onColumnResizing(column.id, $event)"
              (resizeEnd)="onColumnResizeEnd(column.id, $event)"
              [class.header-align-left]="!column.headerAlign"
              [class.header-align-center]="column.headerAlign === 'center'"
              [class.header-align-right]="column.headerAlign === 'right'"
              [class.header-align-left]="column.headerAlign === 'left'"
              [ngClass]="column.headerClass"
              [matTooltip]="translateHeader(column.label)"
              [matTooltipDisabled]="!column.label"
              matTooltipPosition="above"
              [matTooltipShowDelay]="0"
            >
              <div
                class="header-content"
                [class.column-draggable]="isColumnDraggable(column)"
              >
                @if (isColumnDraggable(column)) {
                  <mat-icon
                    class="column-drag-handle"
                    aria-hidden="true"
                    (pointerdown)="
                      onColumnDragHandlePointerDown($event, column, dataColIdx)
                    "
                  >
                    drag_indicator
                  </mat-icon>
                }
                <!-- Custom header template or default label -->
                @if (column.headerTemplate) {
                  <ng-container
                    *ngTemplateOutlet="column.headerTemplate"
                  ></ng-container>
                } @else {
                  <span class="header-label" [ngClass]="column.headerClass">
                    {{ translateHeader(column.label) }}
                  </span>
                }

                <!-- Filter button -->
                @if (column.filterable) {
                  <button
                    mat-icon-button
                    [matMenuTriggerFor]="filterMenu"
                    #menuTrigger="matMenuTrigger"
                    (click)="$event.stopPropagation()"
                    [class.filter-active]="getColumnFilter(column.id) !== null"
                  >
                    <mat-icon
                      [class.active]="getColumnFilter(column.id) !== null"
                    >
                      filter_list
                    </mat-icon>
                  </button>

                  <!-- Filter menu with column filter component -->
                  <mat-menu #filterMenu="matMenu" class="filter-menu">
                    <ng-template matMenuContent>
                      <div (click)="$event.stopPropagation()">
                        <app-column-filter
                          [columnId]="column.id"
                          [columnLabel]="translateHeader(column.label)"
                          [filterType]="
                            $any(column).filterType ||
                            ($any(column).filterAdvancedSearchConfig
                              ? 'advancedSearch'
                              : 'text')
                          "
                          [currentFilter]="getColumnFilter(column.id)"
                          [filterOptions]="column.filterOptions || []"
                          [advancedSearchConfig]="
                            $any(column).filterAdvancedSearchConfig ?? null
                          "
                          [advancedSearchCanEdit]="
                            $any(column).filterAdvancedSearchCanEdit === true
                          "
                          [filterDynamicList]="
                            $any(column).filterDynamicList ?? null
                          "
                          [allowedOperators]="
                            $any(column).allowedOperators ?? null
                          "
                          [menuTrigger]="menuTrigger"
                          (filterApplied)="onFilterApplied(column.id, $event)"
                          (filterCleared)="onFilterCleared(column.id)"
                        ></app-column-filter>
                      </div>
                    </ng-template>
                  </mat-menu>
                }
              </div>
            </th>

            <!-- Cell -->
            <td
              mat-cell
              *matCellDef="let row; let i = index"
              [attr.data-column-id]="column.id"
              [class]="getCellClass(row, column)"
              [class.column-drop-highlight]="isColumnDropHighlighted(column)"
              [class.column-drag-over]="isColumnDragOver(column)"
              [class.tree-embed-first-col]="
                shouldEmbedChildToggleInCell(dataColIdx)
              "
              [style.width]="getColumnWidth(column)"
              [style.min-width]="column.minWidth || '50px'"
              [style.text-align]="column.cellsAlign || 'left'"
              [attr.data-column-id]="column.id"
              [ngStyle]="
                getChildRowCellStyle(row, getDataColumnGlobalIndex(dataColIdx))
              "
            >
              @if (shouldEmbedChildToggleInCell(dataColIdx)) {
                <div
                  class="tree-first-col-inner"
                  [ngStyle]="getEmbeddedTreeIndentStyle(row)"
                >
                  @if (
                    !childRows().hideBuiltInChildToggle &&
                    showChildTreeToggleIcon(row)
                  ) {
                    <mat-icon
                      (click)="
                        onEmbeddedTreeToggleClick(row); $event.stopPropagation()
                      "
                      [attr.aria-label]="
                        isParentRow(row)
                          ? ('components.mat-table.toggleChildren' | transloco)
                          : ('components.mat-table.expandRow' | transloco)
                      "
                      class="child-toggle-icon"
                    >
                      @if (isParentRow(row)) {
                        {{
                          areChildRowsVisible(row)
                            ? 'keyboard_arrow_down'
                            : 'keyboard_arrow_right'
                        }}
                      } @else {
                        {{
                          isRowExpanded(row)
                            ? 'keyboard_arrow_down'
                            : 'keyboard_arrow_right'
                        }}
                      }
                    </mat-icon>
                  } @else if (!childRows().hideBuiltInChildToggle) {
                    <span class="tree-toggle-spacer" aria-hidden="true"></span>
                  }
                  <span class="tree-first-col-value">
                    @if (column.cellTemplate) {
                      <ng-container
                        *ngTemplateOutlet="
                          column.cellTemplate;
                          context: {
                            $implicit: row,
                            row: row,
                            column: column,
                            value: getCellValue(row, column),
                            index: i,
                          }
                        "
                      ></ng-container>
                    } @else {
                      <span
                        [matTooltip]="getCellTooltip(row, column)"
                        [matTooltipDisabled]="!getCellTooltip(row, column)"
                        matTooltipPosition="above"
                        [matTooltipShowDelay]="0"
                      >
                        {{ getCellValue(row, column) }}
                      </span>
                    }
                  </span>
                </div>
              } @else {
                @if (column.cellTemplate) {
                  <ng-container
                    *ngTemplateOutlet="
                      column.cellTemplate;
                      context: {
                        $implicit: row,
                        row: row,
                        column: column,
                        value: getCellValue(row, column),
                        index: i,
                      }
                    "
                  ></ng-container>
                } @else {
                  <span
                    [matTooltip]="getCellTooltip(row, column)"
                    [matTooltipDisabled]="!getCellTooltip(row, column)"
                    matTooltipPosition="above"
                    [matTooltipShowDelay]="0"
                  >
                    {{ getCellValue(row, column) }}
                  </span>
                }
              }
            </td>
          </ng-container>
        }

        <!-- Actions Column - only show if enabled and has visible actions -->
        @if (table().hasVisibleActions()) {
          <ng-container matColumnDef="actions" stickyEnd>
            <th
              mat-header-cell
              *matHeaderCellDef
              class="actions-column"
              [style.--actions-column-width]="getActionsColumnWidth()"
              [hidden]="!showActionsHeader()"
            >
              @if (actions().headerTemplate) {
                <ng-container
                  *ngTemplateOutlet="actions().headerTemplate!"
                ></ng-container>
              }
            </th>
            <td
              mat-cell
              *matCellDef="let row"
              class="actions-column"
              [style.--actions-column-width]="getActionsColumnWidth()"
              [hidden]="!showActions()"
            >
              @if (actions().template) {
                <ng-container
                  *ngTemplateOutlet="actions().template!; context: { row: row }"
                ></ng-container>
              } @else if (actions().actions) {
                @for (action of actions().actions; track $index) {
                  @if (!action.visible || action.visible(row)) {
                    @if (action.icon) {
                      <app-button
                        [variant]="
                          action.icon === 'plus-circle' ? 'filled' : 'icon'
                        "
                        [type]="
                          action.type ??
                          (action.icon === 'plus-circle'
                            ? 'primary'
                            : 'secondary')
                        "
                        [tooltip]="action.tooltip || action.label || ''"
                        [disabledExternal]="
                          action.disabled ? action.disabled(row) : false
                        "
                        (clicked)="
                          action.handler(row); $event.stopPropagation()
                        "
                      >
                        @if (action.pending?.(row)) {
                          <mat-spinner
                            prefix
                            class="mat-table-action-pending-spinner"
                            diameter="20"
                          />
                        } @else if (action.iconFont) {
                          <mat-icon prefix>{{ action.icon }}</mat-icon>
                        } @else {
                          <mat-icon prefix [svgIcon]="action.icon"></mat-icon>
                        }
                      </app-button>
                    } @else if (action.label) {
                      <app-button
                        variant="text"
                        [label]="action.label"
                        [tooltip]="action.tooltip || ''"
                        [disabledExternal]="
                          action.disabled ? action.disabled(row) : false
                        "
                        (clicked)="
                          action.handler(row); $event.stopPropagation()
                        "
                      />
                    }
                  }
                }
              }
            </td>
          </ng-container>
        }

        <!-- Expanded Row Detail spacer (height-only; content lives in .row-detail-float) -->
        @if (rowExpansion().enabled) {
          <ng-container matColumnDef="expandedDetail">
            <td
              mat-cell
              *matCellDef="let row"
              [attr.colspan]="layoutDisplayedColumns().length"
              class="expanded-detail-cell"
              [attr.data-spacer-row-id]="getRowId(row)"
              [style.height.px]="getDetailSpacerHeight(row)"
            ></td>
          </ng-container>
        }

        <!-- Header Row -->
        <tr
          mat-header-row
          *matHeaderRowDef="
            dragPreviewDisplayedColumns();
            sticky: !columnReorder().enabled
          "
        ></tr>

        <!-- Data Rows -->
        <tr
          mat-row
          *matRowDef="let row; columns: dragPreviewDisplayedColumns()"
          [class.clickable]="
            (rowExpansion().enabled &&
              rowExpansion().expandOnRowClick &&
              isExpandableRow(row)) ||
            (childRows().enabled &&
              childRows().toggleOnRowClick &&
              isParentRow(row))
          "
          [class.expanded]="isRowExpanded(row)"
          [class.striped-even-row]="
            appearance().striped && isStripedEvenRow(row)
          "
          [ngClass]="getRowClass(row)"
          [hidden]="!table().isRowVisible(row)"
          (click)="onRowClick(row, $event)"
        ></tr>

        <!-- Expanded Detail Row (only for open/closing rows — avoid 2× DOM at 500/1000) -->
        @if (rowExpansion().enabled) {
          <tr
            mat-row
            *matRowDef="
              let row;
              columns: ['expandedDetail'];
              when: isExpansionDetailRow
            "
            class="detail-row"
          ></tr>
        }
      </table>
      @if (data().length === 0) {
        <div class="no-data-row">
          <div>{{ 'core.noData' | transloco }}</div>
        </div>
      }
    }

    @if (virtualBottomPad() > 0) {
      <div
        class="virtual-scroll-pad"
        [style.height.px]="virtualBottomPad()"
        aria-hidden="true"
      ></div>
    }
  </div>

  <!-- Paginator -->
  @if (showPagination()) {
    <mat-paginator
      [length]="totalCount()"
      [pageSize]="pagination().pageSize"
      [pageSizeOptions]="pagination().pageSizeOptions"
      [pageIndex]="getCurrentPageIndex()"
      [showFirstLastButtons]="pagination().showFirstLastButtons"
      [selectConfig]="paginatorSelectConfig"
      (page)="onPageChange($event)"
      class="sticky-footer"
    ></mat-paginator>
  }
</div>
